import PropTypes from 'prop-types';
import { Box, Skeleton, Typography } from '@mui/material';
import QuadPanel from 'features/IRWorkstation/components/Common/QuadPanel';
import useAccountInteractionActivity from 'features/IRWorkstation/hooks/useAccountInteractionActivity';

const StatRow = ({ label, count, strong = false }) => (
	<Box
		sx={{
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			padding: '10px 15px',
			borderBottom: '0.5px solid',
			borderColor: 'border.light',
		}}
	>
		<Typography
			sx={{
				fontSize: '12px',
				fontWeight: strong ? 700 : 400,
				color: 'text.primary',
			}}
		>
			{label}
		</Typography>
		<Typography
			sx={{
				fontSize: '12px',
				fontWeight: strong ? 700 : 500,
				color: 'text.primary',
			}}
		>
			{count}
		</Typography>
	</Box>
);

StatRow.propTypes = {
	label: PropTypes.string.isRequired,
	count: PropTypes.number.isRequired,
	strong: PropTypes.bool,
};

const StatRowSkeleton = () => (
	<Box
		data-testid='lp-activity-row-skeleton'
		sx={{
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			padding: '10px 15px',
			borderBottom: '0.5px solid',
			borderColor: 'border.light',
		}}
	>
		<Skeleton variant='text' width='50%' sx={{ fontSize: '12px' }} />
		<Skeleton variant='text' width={28} sx={{ fontSize: '12px' }} />
	</Box>
);

const LPActivityOverviewCard = ({ accountId }) => {
	const { totalTouchpoints, typeCounts, loading, error } =
		useAccountInteractionActivity(accountId);

	return (
		<Box
			data-testid='lp-activity-overview-card'
			sx={{
				width: '100%',
				height: '100%',
				minWidth: 0,
				minHeight: 0,
				display: 'flex',
				'& > *': { flex: 1 },
			}}
		>
			<QuadPanel title='Activity overview'>
				{error ? (
					<Box
						sx={{
							minHeight: 200,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<Typography sx={{ fontSize: '12px' }} color='error'>
							Unable to load Interaction records
						</Typography>
					</Box>
				) : loading ? (
					<Box>
						{Array.from({ length: 3 }, (_, index) => (
							<StatRowSkeleton key={index} />
						))}
					</Box>
				) : (
					<Box>
						<StatRow
							label='Total touchpoints'
							count={totalTouchpoints ?? 0}
							strong
						/>
						{typeCounts.map(({ type, count }) => (
							<StatRow key={type} label={type} count={count} />
						))}
					</Box>
				)}
			</QuadPanel>
		</Box>
	);
};

LPActivityOverviewCard.propTypes = {
	accountId: PropTypes.string.isRequired,
};

export default LPActivityOverviewCard;
